/** @format */

import {
  BarChartOutlined,
  CopyOutlined,
  FileSearchOutlined,
  LeftOutlined,
  PrinterOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Alert, Button, DatePicker, Input, Segmented, Select, Spin, Switch } from "antd";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { API_ENDPOINTS } from "../../../constants/api";
import { actionGroupsListRequest } from "../../../redux-modules/actions/actionsGroups";
import { TAPIGroupsListRequest } from "../../../redux-modules/declarations/apis/groups";
import { TUser } from "../../../redux-modules/declarations/maps/auth";
import { TPersonaSettimana } from "../../../redux-modules/declarations/maps/turniSettimana";
import apiClient from "../../../utils/apiClient";
import { showNotification } from "../../../utils/showNotification";
import { CheckShiftModal } from "../../layout/SideDialogs/ShiftCheck";
import { ShiftLastWeekCheckModal } from "../../layout/SideDialogs/ShiftLastWeekCheck";
import { RepeatShiftModal } from "../../layout/SideDialogs/ShiftRepeat";
import UITransition from "../../ui/Transition";
import {
  corrispondeAlFiltro,
  corrispondeAllaRicerca,
  haAvvisi,
  sottoContratto,
  TFiltroPersone,
  TUnita,
} from "./format";
import { AssegnazioneRapida } from "./partials/AssegnazioneRapida";
import { DettaglioTurno } from "./partials/DettaglioTurno";
import { GrigliaSettimana, TGruppoVista } from "./partials/GrigliaSettimana";
import { BarraStrumenti, C, Intestazione, Legenda, Pagina } from "./styles";
import { messaggioErrore, useTurniSettimana } from "./useTurniSettimana";

const CHIAVE_PREFERENZE = "hr.turniSettimana";

/** "pulita": solo la pianificazione; "segnalazioni": anche avvisi del contratto e anomalie. */
type TVistaSegnalazioni = "segnalazioni" | "pulita";

interface TPreferenze {
  unita: TUnita;
  gruppi: string[];
  reparti: string[];
  chiusi: string[];
  vista: TVistaSegnalazioni;
}

const PREFERENZE_INIZIALI: TPreferenze = {
  unita: "ore",
  gruppi: [],
  reparti: [],
  chiusi: [],
  vista: "segnalazioni",
};

/** Preferenze della vista nel browser: unità, filtri e gruppi chiusi restano tra una visita e l'altra. */
const leggiPreferenze = (): TPreferenze => {
  try {
    const salvate = JSON.parse(window.localStorage.getItem(CHIAVE_PREFERENZE) || "{}");
    return { ...PREFERENZE_INIZIALI, ...salvate };
  } catch (e) {
    return PREFERENZE_INIZIALI;
  }
};

const salvaPreferenze = (preferenze: TPreferenze): void => {
  try {
    window.localStorage.setItem(CHIAVE_PREFERENZE, JSON.stringify(preferenze));
  } catch (e) {
    // archiviazione non disponibile: le preferenze valgono solo per questa visita
  }
};

const chiaveId = (id: number | null): string => (id === null ? "nessuno" : String(id));

const etichettaSettimana = (lunedi: Moment): string => {
  const domenica = lunedi.clone().add(6, "days");
  return lunedi.month() === domenica.month()
    ? `${lunedi.format("D")} \u2013 ${domenica.format("D MMMM YYYY")}`
    : `${lunedi.format("D MMM")} \u2013 ${domenica.format("D MMM YYYY")}`;
};

interface TAssegnazioneAperta {
  personaId: number;
  data: string;
  ancora: DOMRect;
}

interface TDettaglioAperto {
  personaId: number;
  turnoId: number;
}

export const ViewTurniSettimana = (): JSX.Element => {
  const dispatch = useDispatch();
  const [preferenze, setPreferenze] = useState<TPreferenze>(leggiPreferenze);
  const [giorno, setGiorno] = useState<Moment>(moment());
  const [ricerca, setRicerca] = useState("");
  const [filtro, setFiltro] = useState<TFiltroPersone>("tutti");
  const [assegnazione, setAssegnazione] = useState<TAssegnazioneAperta | null>(null);
  const [dettaglio, setDettaglio] = useState<TDettaglioAperto | null>(null);
  const [finestra, setFinestra] = useState<null | "replica" | "verifica" | "report">(null);

  const lunedi = useMemo(() => giorno.clone().isoWeekday(1).startOf("day"), [giorno]);
  const {
    dati,
    caricamento,
    aggiornamento,
    errore,
    tipiTurno,
    sedi,
    ricarica,
    assegna,
    elimina,
    cambiaOrario,
    accettaCambio,
    rifiutaCambio,
    salvaNota,
    eliminaNota,
    replica,
  } = useTurniSettimana(lunedi.format("YYYY-MM-DD"));

  // le finestre esistenti (replica, verifica, report) leggono i gruppi dallo store
  useEffect(() => {
    // page_size non è nel tipo della richiesta ma l'API lo usa (come nella vista precedente)
    dispatch(actionGroupsListRequest(({ page_size: 100 } as unknown) as TAPIGroupsListRequest));
  }, [dispatch]);

  const aggiornaPreferenze = useCallback((modifica: Partial<TPreferenze>): void => {
    setPreferenze(attuali => {
      const nuove = { ...attuali, ...modifica };
      salvaPreferenze(nuove);
      return nuove;
    });
  }, []);

  const oggi = dati?.settimana.oggi || moment().format("YYYY-MM-DD");
  const permessi = dati?.permessi || {
    crea: false,
    elimina: false,
    gestisci_cambi: false,
    crea_passato: false,
    elimina_passato: false,
  };
  const pulita = preferenze.vista === "pulita";

  // in vista pulita non ci sono mancate timbrature né avvisi da filtrare
  const cambiaVista = (mostraSegnalazioni: boolean): void => {
    aggiornaPreferenze({ vista: mostraSegnalazioni ? "segnalazioni" : "pulita" });
    if (!mostraSegnalazioni) setFiltro(f => (f === "mancate" || f === "avvisi" ? "tutti" : f));
  };

  // ------------------------------------------------------------------ filtri

  const opzioniGruppi = useMemo(
    () => (dati ? dati.gruppi.map(g => ({ value: chiaveId(g.id), label: g.nome })) : []),
    [dati],
  );
  const opzioniReparti = useMemo(() => {
    if (!dati) return [];
    const visti: Record<string, string> = {};
    dati.persone.forEach(p => {
      visti[chiaveId(p.reparto_id)] = p.reparto || "Senza reparto";
    });
    return Object.keys(visti)
      .map(k => ({ value: k, label: visti[k] }))
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [dati]);

  // filtri salvati che non esistono più (gruppo eliminato) non devono svuotare la vista
  const gruppiSel = useMemo(
    () => preferenze.gruppi.filter(g => opzioniGruppi.some(o => o.value === g)),
    [preferenze.gruppi, opzioniGruppi],
  );
  const repartiSel = useMemo(
    () => preferenze.reparti.filter(r => opzioniReparti.some(o => o.value === r)),
    [preferenze.reparti, opzioniReparti],
  );
  const filtrando = !!ricerca.trim() || filtro !== "tutti";

  const vista = useMemo(() => {
    const vuota = { gruppi: [] as TGruppoVista[], conteggi: { mancate: 0, avvisi: 0, sotto: 0 } };
    if (!dati) return vuota;

    const perId: Record<number, TPersonaSettimana> = {};
    dati.persone.forEach(p => {
      perId[p.id] = p;
    });
    const nelPerimetro = (p: TPersonaSettimana): boolean =>
      (gruppiSel.length === 0 || gruppiSel.indexOf(chiaveId(p.gruppo_id)) >= 0) &&
      (repartiSel.length === 0 || repartiSel.indexOf(chiaveId(p.reparto_id)) >= 0);
    const visibile = (p: TPersonaSettimana): boolean =>
      nelPerimetro(p) && corrispondeAlFiltro(p, filtro) && corrispondeAllaRicerca(p, ricerca);

    const perimetro = dati.persone.filter(nelPerimetro);
    const conteggi = {
      mancate: perimetro.reduce((n, p) => n + p.totali.mancate_timbrature, 0),
      avvisi: perimetro.filter(haAvvisi).length,
      sotto: perimetro.filter(sottoContratto).length,
    };

    const gruppi = dati.gruppi.map(
      (gruppo): TGruppoVista => {
        const chiave = chiaveId(gruppo.id);
        const reparti = gruppo.reparti.map(reparto => ({
          reparto,
          persone: reparto.persone
            .map(id => perId[id])
            .filter((p): p is TPersonaSettimana => !!p && visibile(p)),
        }));
        return {
          chiave,
          gruppo,
          // con una ricerca o un filtro i gruppi si aprono, per mostrare i risultati
          aperto: filtrando || preferenze.chiusi.indexOf(chiave) < 0,
          personeVisibili: reparti.reduce((n, r) => n + r.persone.length, 0),
          reparti,
        };
      },
    );

    return { gruppi, conteggi };
  }, [dati, gruppiSel, repartiSel, filtro, ricerca, filtrando, preferenze.chiusi]);

  // ------------------------------------------------------------------ azioni

  const apriAssegnazione = useCallback((personaId: number, data: string, ancora: DOMRect): void => {
    setDettaglio(null);
    setAssegnazione({ personaId, data, ancora });
  }, []);
  const apriTurno = useCallback((personaId: number, turnoId: number): void => {
    setAssegnazione(null);
    setDettaglio({ personaId, turnoId });
  }, []);
  const chiudiAssegnazione = useCallback((): void => setAssegnazione(null), []);
  const chiudiDettaglio = useCallback((): void => setDettaglio(null), []);

  const apriChiudiGruppo = useCallback(
    (chiave: string): void =>
      setPreferenze(attuali => {
        const chiusi =
          attuali.chiusi.indexOf(chiave) >= 0
            ? attuali.chiusi.filter(c => c !== chiave)
            : [...attuali.chiusi, chiave];
        const nuove = { ...attuali, chiusi };
        salvaPreferenze(nuove);
        return nuove;
      }),
    [],
  );

  const azzeraFiltri = useCallback((): void => {
    setRicerca("");
    setFiltro("tutti");
  }, []);

  const idGruppiPerStampa = (): string[] =>
    (gruppiSel.length ? gruppiSel : opzioniGruppi.map(o => o.value)).filter(v => v !== "nessuno");
  const idRepartiPerStampa = (): string[] =>
    (repartiSel.length ? repartiSel : opzioniReparti.map(o => o.value)).filter(
      v => v !== "nessuno",
    );

  const stampa = (): void => {
    const dal = lunedi.format("YYYY-MM-DD");
    const al = lunedi.clone().add(6, "days").format("YYYY-MM-DD");
    apiClient
      .post(
        API_ENDPOINTS.SHIFTS + "/print",
        {
          group_ids: idGruppiPerStampa(),
          department_ids: idRepartiPerStampa(),
          date_from: dal,
          date_to: al,
        },
        { responseType: "blob" },
      )
      .then(res => {
        const url = URL.createObjectURL(
          new Blob([res.data as BlobPart], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.style.display = "none";
        link.href = url;
        link.setAttribute("download", `Turni ${dal} - ${al}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 10000);
      })
      .catch(err =>
        showNotification(
          "error",
          "Stampa non riuscita",
          messaggioErrore(err, "Errore durante la stampa"),
        ),
      );
  };

  // la finestra di replica usa solo id, nome e cognome
  const utentiPerReplica = useMemo(
    () =>
      ((dati?.persone || []).map(p => ({
        id: String(p.id),
        name: p.nome,
        surname: p.cognome,
      })) as unknown) as TUser[],
    [dati],
  );

  const personaAssegnazione =
    assegnazione && dati?.persone.find(p => p.id === assegnazione.personaId);
  const personaDettaglio = dettaglio && dati?.persone.find(p => p.id === dettaglio.personaId);
  let turnoDettaglio = null;
  let dataDettaglio = "";
  if (personaDettaglio && dettaglio) {
    for (const g of personaDettaglio.giorni) {
      const trovato = g.turni.find(t => t.id === dettaglio.turnoId);
      if (trovato) {
        turnoDettaglio = trovato;
        dataDettaglio = g.data;
        break;
      }
    }
  }

  // ------------------------------------------------------------------ resa

  const conteggio = (n: number, colore: string): JSX.Element => (
    <span className={`conteggio ${colore}`}>{n}</span>
  );

  return (
    <UITransition>
      <Pagina>
        <Intestazione>
          <div>
            <h1>Turni</h1>
            <div className="sottotitolo">Vista settimanale</div>
          </div>
          {permessi.crea && (
            <div className="azioni">
              <Button icon={<CopyOutlined />} onClick={(): void => setFinestra("replica")}>
                Replica turni
              </Button>
              <Button icon={<FileSearchOutlined />} onClick={(): void => setFinestra("verifica")}>
                Verifica
              </Button>
              <Button icon={<BarChartOutlined />} onClick={(): void => setFinestra("report")}>
                Report
              </Button>
              <Button icon={<PrinterOutlined />} onClick={stampa}>
                Stampa
              </Button>
            </div>
          )}
        </Intestazione>

        <BarraStrumenti>
          <div className="riga">
            <div className="settimana">
              <Button
                icon={<LeftOutlined />}
                aria-label="Settimana precedente"
                onClick={(): void => setGiorno(g => g.clone().subtract(1, "week"))}
              />
              <DatePicker
                picker="week"
                allowClear={false}
                value={lunedi}
                onChange={(v): void => {
                  if (v) setGiorno(v);
                }}
                format={(): string => etichettaSettimana(lunedi)}
                className="etichetta-settimana"
                aria-label="Settimana"
              />
              <Button
                icon={<RightOutlined />}
                aria-label="Settimana successiva"
                onClick={(): void => setGiorno(g => g.clone().add(1, "week"))}
              />
              <Button onClick={(): void => setGiorno(moment())}>Oggi</Button>
            </div>
            <Select
              className="filtro"
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder="Tutti i gruppi"
              aria-label="Gruppi"
              value={gruppiSel}
              options={opzioniGruppi}
              optionFilterProp="label"
              onChange={(v: string[]): void => aggiornaPreferenze({ gruppi: v })}
            />
            <Select
              className="filtro"
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder="Tutti i reparti"
              aria-label="Reparti"
              value={repartiSel}
              options={opzioniReparti}
              optionFilterProp="label"
              onChange={(v: string[]): void => aggiornaPreferenze({ reparti: v })}
            />
            <Input
              className="ricerca"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cerca dipendente"
              aria-label="Cerca dipendente"
              value={ricerca}
              onChange={(e): void => setRicerca(e.target.value)}
            />
            <div className="spazio" />
            {aggiornamento && <Spin size="small" />}
            <Segmented
              aria-label="Unità dei totali"
              value={preferenze.unita}
              options={[
                { label: "Ore", value: "ore" },
                { label: "Minuti", value: "minuti" },
              ]}
              onChange={(v): void => aggiornaPreferenze({ unita: v as TUnita })}
            />
          </div>
          <div className="riga">
            <span className="mostra">Mostra</span>
            <Segmented
              value={filtro}
              onChange={(v): void => setFiltro(v as TFiltroPersone)}
              options={[
                { label: "Tutti", value: "tutti" },
                ...(pulita
                  ? []
                  : [
                      {
                        label: (
                          <span>
                            Mancate timbrature{conteggio(vista.conteggi.mancate, "rosso")}
                          </span>
                        ),
                        value: "mancate",
                      },
                      {
                        label: (
                          <span>
                            Avvisi di contratto{conteggio(vista.conteggi.avvisi, "ambra")}
                          </span>
                        ),
                        value: "avvisi",
                      },
                    ]),
                {
                  label: <span>Sotto contratto{conteggio(vista.conteggi.sotto, "blu")}</span>,
                  value: "sotto",
                },
              ]}
            />
            <div className="spazio" />
            <label className="interruttore">
              <Switch size="small" checked={!pulita} onChange={cambiaVista} />
              <span>Mostra avvisi e anomalie</span>
            </label>
          </div>
        </BarraStrumenti>

        {errore && (
          <Alert
            type="error"
            showIcon
            message={errore}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
                Riprova
              </Button>
            }
          />
        )}

        <Spin spinning={caricamento}>
          {dati ? (
            <GrigliaSettimana
              giorni={dati.giorni}
              gruppi={vista.gruppi}
              oggi={oggi}
              unita={preferenze.unita}
              filtrando={filtrando}
              puoCreare={permessi.crea}
              puoCrearePassato={permessi.crea_passato}
              pulita={pulita}
              onScroll={chiudiAssegnazione}
              onApriChiudiGruppo={apriChiudiGruppo}
              onFiltro={setFiltro}
              onAzzeraFiltri={azzeraFiltri}
              onApriAssegnazione={apriAssegnazione}
              onApriTurno={apriTurno}
            />
          ) : (
            <div style={{ minHeight: 320 }} />
          )}
        </Spin>

        <Legenda aria-label="Legenda">
          <span>
            <i style={{ background: C.daSvolgere.fondo, borderColor: C.daSvolgere.bordo }} />
            Da svolgere
          </span>
          <span>
            <i style={{ background: C.inCorso.fondo, borderColor: C.inCorso.bordo }} />
            In corso
          </span>
          <span>
            <i style={{ background: C.timbrato.fondo, borderColor: C.timbrato.bordo }} />
            Timbrato, non modificabile
          </span>
          {!pulita && (
            <>
              <span>
                <i style={{ background: C.anomalia.fondo, borderColor: C.anomalia.bordo }} />
                Mancata timbratura
              </span>
              <span>
                <i
                  style={{
                    background: C.daSvolgere.fondo,
                    borderColor: C.avviso.bordo,
                    borderWidth: 2,
                  }}
                />
                Avviso di contratto
              </span>
            </>
          )}
          <span>
            <i style={{ background: C.riposo.fondo, borderColor: C.riposo.fondo }} />
            Riposo
          </span>
          <span>
            <i style={{ background: "rgba(229, 158, 110, 0.22)", borderColor: "transparent" }} />
            Ferie e permessi
          </span>
          <span>
            <i
              style={{
                backgroundColor: "#F6F7F9",
                backgroundImage:
                  "repeating-linear-gradient(135deg, transparent 0 4px, rgba(110,116,140,.25) 4px 5px)",
                borderColor: C.linea,
              }}
            />
            Giorno non previsto dal contratto
          </span>
        </Legenda>
      </Pagina>

      {assegnazione && personaAssegnazione && (
        <AssegnazioneRapida
          persona={personaAssegnazione}
          data={assegnazione.data}
          ancora={assegnazione.ancora}
          tipiTurno={tipiTurno}
          sedi={sedi}
          onChiudi={chiudiAssegnazione}
          onAssegna={(tipoTurnoId, nascondiFine, sedeId, nota): Promise<boolean> =>
            assegna({
              user_id: personaAssegnazione.id,
              shift_type_id: tipoTurnoId,
              date: assegnazione.data,
              office_id: sedeId,
              hidden: nascondiFine,
              notes: nota.trim() || undefined,
            })
          }
        />
      )}

      {personaDettaglio && turnoDettaglio && (
        <DettaglioTurno
          persona={personaDettaglio}
          turno={turnoDettaglio}
          data={dataDettaglio}
          oggi={oggi}
          permessi={permessi}
          tipiTurno={tipiTurno}
          sedi={sedi}
          onChiudi={chiudiDettaglio}
          onElimina={elimina}
          onCambiaOrario={cambiaOrario}
          onAccettaCambio={accettaCambio}
          onRifiutaCambio={rifiutaCambio}
          onSalvaNota={salvaNota}
          onEliminaNota={eliminaNota}
        />
      )}

      {finestra === "replica" && (
        <RepeatShiftModal
          isModalVisible
          onOk={replica}
          closeModal={(): void => setFinestra(null)}
          userList={utentiPerReplica}
          currentDateSelected={lunedi}
        />
      )}
      {finestra === "verifica" && (
        <CheckShiftModal
          isModalVisible
          selectedGroups={idGruppiPerStampa()}
          selectedDepartments={idRepartiPerStampa()}
          selectedDate={lunedi}
          closeModal={(): void => setFinestra(null)}
        />
      )}
      {finestra === "report" && (
        <ShiftLastWeekCheckModal
          isModalVisible
          selectedGroups={idGruppiPerStampa()}
          selectedDepartments={idRepartiPerStampa()}
          closeModal={(): void => setFinestra(null)}
        />
      )}
    </UITransition>
  );
};
